import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    CircularProgress,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

import { MedicalActTemplate } from '@/core/models/medical-act.model'
import {
    useGetMedicalActTemplatesQuery,
    useDeleteMedicalActTemplateMutation,
} from '@/core/services/medical-act'
import { formatPrice } from '@/utils/formatPrice'

import { MedicalActTemplateForm } from './MedicalActTemplateForm'

export function MedicalActTemplates() {
    const { t } = useTranslation()
    const [formOpen, setFormOpen] = useState(false)
    const [editingTemplate, setEditingTemplate] = useState<MedicalActTemplate | null>(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [templateToDelete, setTemplateToDelete] = useState<MedicalActTemplate | null>(null)

    const { data: templates, isLoading, error } = useGetMedicalActTemplatesQuery()
    const [deleteTemplate, { isLoading: isDeleting }] = useDeleteMedicalActTemplateMutation()

    const handleCreate = () => {
        setEditingTemplate(null)
        setFormOpen(true)
    }

    const handleEdit = (template: MedicalActTemplate) => {
        setEditingTemplate(template)
        setFormOpen(true)
    }

    const handleFormClose = () => {
        setFormOpen(false)
        setEditingTemplate(null)
    }

    const handleDeleteClick = (template: MedicalActTemplate) => {
        setTemplateToDelete(template)
        setDeleteDialogOpen(true)
    }

    const handleDeleteConfirm = async () => {
        if (templateToDelete) {
            await deleteTemplate(templateToDelete._id)
            setDeleteDialogOpen(false)
            setTemplateToDelete(null)
        }
    }

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        )
    }

    if (error) {
        return (
            <Alert severity="error">
                {t('common.error.loading', { defaultValue: 'Error loading data' })}
            </Alert>
        )
    }

    return (
        <Box>
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                >
                    {t('page.admin.medicalActTemplates.create', { defaultValue: 'Create Template' })}
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('common.name', { defaultValue: 'Name' })}</TableCell>
                            <TableCell>{t('common.code', { defaultValue: 'Code' })}</TableCell>
                            <TableCell align="right">{t('common.price', { defaultValue: 'Price' })}</TableCell>
                            <TableCell>{t('common.type', { defaultValue: 'Type' })}</TableCell>
                            <TableCell>{t('common.status', { defaultValue: 'Status' })}</TableCell>
                            <TableCell align="center">{t('common.actions', { defaultValue: 'Actions' })}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {templates?.map((template) => (
                            <TableRow key={template._id}>
                                <TableCell>
                                    <Typography fontWeight="medium">{template.name}</Typography>
                                    {template.description && (
                                        <Typography variant="body2" color="text.secondary">
                                            {template.description}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <code>{template.code}</code>
                                </TableCell>
                                <TableCell align="right">
                                    {formatPrice(template.price)}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={template.isGlobal ? t('common.global', { defaultValue: 'Global' }) : t('common.local', { defaultValue: 'Local' })}
                                        color={template.isGlobal ? 'primary' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={template.isActive ? t('common.active', { defaultValue: 'Active' }) : t('common.inactive', { defaultValue: 'Inactive' })}
                                        color={template.isActive ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleEdit(template)}
                                        title={t('common.edit', { defaultValue: 'Edit' })}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteClick(template)}
                                        title={t('common.delete', { defaultValue: 'Delete' })}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {templates?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography color="text.secondary" py={4}>
                                        {t('page.admin.medicalActTemplates.empty', { defaultValue: 'No templates found. Create your first template.' })}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create/Edit Form Dialog */}
            <MedicalActTemplateForm
                open={formOpen}
                onClose={handleFormClose}
                template={editingTemplate}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>
                    {t('page.admin.medicalActTemplates.deleteConfirm.title', { defaultValue: 'Delete Template' })}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('page.admin.medicalActTemplates.deleteConfirm.message', {
                            defaultValue: 'Are you sure you want to delete the template "{{name}}"?',
                            name: templateToDelete?.name,
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                        {t('common.cancel', { defaultValue: 'Cancel' })}
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
                        {isDeleting ? <CircularProgress size={20} /> : t('common.delete', { defaultValue: 'Delete' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default MedicalActTemplates
